<script>
/* eslint-disable no-undef */
import { cloneDeep } from 'lodash-es'

export default {
    name: 'List',

    props: {
        viewSelectorHeight: {
            type: Number,
            required: true
        },
        zenHeaderHeight: {
            type: Number,
            required: true
        },
        dataSet: {
            type: Object,
            required: true
        },
        calendar: {
            type: Object,
            required: true
        },
        mode: {
            type: String,
            required: false,
            default: () => 'web'
        },
    },

    data() {
        return {
            showMore: null,
            showPrevDaysInMonth: false,
            showHidePrevDaysButton: false,
        }
    },

    watch: {
        'dataSet.occasions': {
            handler(newVal, oldVal) {
                if ( newVal !== oldVal ) {
                    this.initShowMoreState()
                }
            }
        },

        'dataSet.navMonthStart': {
            handler(newVal, oldVal) {
                if ( newVal !== oldVal ) {
                    this.showPrevDaysInMonth = ! this.isCurrentMonth(newVal._d)
                    this.showHidePrevDaysButton = this.isCurrentMonth(newVal._d)
                }
            }
        },
    },

    mounted() {
        this.initShowMoreState()
    },

    methods: {
        getDisplayStart() {
            const displayStart = cloneDeep(this.dataSet.navMonthStart)
            displayStart.startOf('week').startOf('day')

            return displayStart
        },

        getDisplayDays() {
            const displayEnd = cloneDeep(this.dataSet.navMonthStart)
            displayEnd.endOf('month')
            displayEnd.endOf('week').startOf('day')

            const displayDays = displayEnd.diff(this.getDisplayStart(), 'days') + 1

            return displayDays
        },

        getDateForDisplay(dayIdx) {
            const dateForDisplay = cloneDeep(this.getDisplayStart())
            dateForDisplay.add(dayIdx, 'd')
            return dateForDisplay
        },

        beforeMonthStartOrAfterMonthEnd(date) {
            const monthEnd = cloneDeep(this.dataSet.navMonthStart)
            monthEnd.endOf('month')
            return date.isBefore(this.dataSet.navMonthStart) || date.isAfter(monthEnd)
        },

        getOccasionsForDayIdx(dayIdx) {
            return this.calendar.getOccasionsForDay(this.dateFormatDayISO(this.getDateForDisplay(dayIdx)))
        },

        initShowMoreState() {
            const _displayDays = this.getDisplayDays()

            this.showMore = []
            for (var i = 1; i <= _displayDays; i++) {
                this.showMore.push(false)
            }
        },

        switchMoreEventsForDay(dayIdx) {
            this.$set(this.showMore, dayIdx, !this.showMore[dayIdx])
        },

        isPrevDayInMonth(date) {
            return (new Date(date).getDate() < new Date().getDate())
        },

        isCurrentMonth(date) {
            return (new Date(date).getMonth() === new Date().getMonth())
        },
    }
}
</script>

<template>
    <div>
        <div
            class="container-fluid"
            :style="{
                position: 'sticky',
                top: zenHeaderHeight+viewSelectorHeight+'px',
                backgroundColor: 'white',
                zIndex: 41
            }"
        >
            <div class="container">
                <monthly-nav
                    :zen-header-height="zenHeaderHeight"
                    :view-selector-height="viewSelectorHeight"
                    :data-set="dataSet"
                    v-on="$listeners"
                />
            </div>
        </div>
        <div class="container-fluid">
            <div class="container">
                <div class="monthly-display-list pt-4">
                    <div v-if="! showPrevDaysInMonth"
                         class="text-center"
                    >
                        <button class="btn btn-primary mb-4"
                                type="button"
                                @click="showPrevDaysInMonth = true; showHidePrevDaysButton = true"
                        >
                            {{ __('Korábbi események') }}
                        </button>
                    </div>

                    <div v-if="showPrevDaysInMonth && showHidePrevDaysButton"
                         class="text-center"
                    >
                        <button class="btn btn-primary mb-4"
                                type="button"
                                @click="showPrevDaysInMonth = false; showHidePrevDaysButton = false"
                        >
                            {{ __('Vissza az aktuális eseményekhez') }}
                        </button>
                    </div>


                    <div
                        v-for="(displayDay, dayIdx) in getDisplayDays()"
                        :key="dayIdx"
                    >
                        <!-- DESKTOP BELOW -->
                        <div class="d-none">
                            <div
                                :class="{ 'inactive' : beforeMonthStartOrAfterMonthEnd(getDateForDisplay(dayIdx))}"
                            >
                                <div
                                    class="text-uppercase dayname"
                                >
                                    {{ dateFormatDayname(getDateForDisplay(dayIdx)) }}
                                </div>
                                <div class="daynumber pb-3 pb-sm-0">
                                    <span
                                        class="day-number-circle"
                                        @click="$emit('go-to-day', getDateForDisplay(dayIdx))"
                                    >
                                        {{ dateFormatDaynumber(getDateForDisplay(dayIdx)) }}
                                    </span>
                                </div>
                            </div>
                            <!-- FIRST 4 ITEM BELOW -->
                            <div
                                v-for="(occasion, occasionIdx) in getOccasionsForDayIdx(dayIdx).slice(0, 4)"
                                :key="occasionIdx"
                            >
                                <div class="mb-4">
                                    <event-item-preview
                                        monthly-view
                                        :occasion="occasion"
                                        reveal-on-hover
                                    />
                                </div>
                            </div>
                            <!-- MORE ITEMS THAN 4 BELOW -->
                            <div v-if="getOccasionsForDayIdx(dayIdx).length > 4">
                                <transition-expand>
                                    <div v-show="showMore && showMore[dayIdx]">
                                        <div
                                            v-for="(occasion, occasionIdx) in getOccasionsForDayIdx(dayIdx).slice(4)"
                                            :key="occasionIdx"
                                        >
                                            <div class="mb-4">
                                                <event-item-preview
                                                    monthly-view
                                                    :occasion="occasion"
                                                    reveal-on-hover
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </transition-expand>
                                <div
                                    v-if="showMore && showMore[dayIdx]"
                                    @click="switchMoreEventsForDay(dayIdx)"
                                >
                                    <div class="show-more d-flex align-items-center justify-content-between pb-3">
                                        <div>
                                            {{ __('Show less') }}
                                        </div>
                                        <div>
                                            <svg class="icon icon-cal-show-less">
                                                <use xlink:href="#cal-show-more" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-else
                                    @click="switchMoreEventsForDay(dayIdx)"
                                >
                                    <div class="show-more d-flex align-items-center justify-content-between pb-3">
                                        <div>
                                            {{ __('More events') }}
                                        </div>
                                        <div>
                                            <svg class="icon icon-cal-show-more">
                                                <use xlink:href="#cal-show-more" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- DESKTOP BELOW -->
                        <div class="d-block">
                            <div
                                v-if="getOccasionsForDayIdx(dayIdx).length"
                                :class="{ 'd-none' : beforeMonthStartOrAfterMonthEnd(getDateForDisplay(dayIdx))}"
                            >
                                <div>
                                    <!-- ITEMS BELOW -->
                                    <div
                                        v-for="(occasion, occasionIdx) in getOccasionsForDayIdx(dayIdx)"
                                        :key="occasionIdx"
                                    >
                                        <div v-if="showPrevDaysInMonth">
                                            <event-item-list
                                                v-if="isPrevDayInMonth(occasion.date)"
                                                :occasion="occasion"
                                                :month-name="dateFormatMonthName(getDateForDisplay(dayIdx))"
                                                :day-number="dateFormatDaynumber(getDateForDisplay(dayIdx))"
                                                :mode="mode"
                                            />
                                        </div>

                                        <event-item-list
                                            v-if="! isPrevDayInMonth(occasion.date)"
                                            :occasion="occasion"
                                            :month-name="dateFormatMonthName(getDateForDisplay(dayIdx))"
                                            :day-number="dateFormatDaynumber(getDateForDisplay(dayIdx))"
                                            :mode="mode"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- <monthly-nav
                    bottom-nav
                    :data-set="dataSet"
                    disable-border-top
                    v-on="$listeners"
                /> -->
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "./resources/sass/_variables.scss";

.show-more {
    font-size: 11px;
    color: $zh-turquoise;
    font-weight: bold;
    cursor: pointer;
}

::v-deep .v-popper {
    width: 100% !important;
}
</style>
